<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col md="12">
          <h5 class="section-label mb-3">
            {{ $t('Configuration') }}
          </h5>
        </b-col>
        <b-col
          md="12"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <b-form>
            <b-row class="match-height">
              <b-col
                md="2"
                class="mt-2"
              >
                <SwitchButton
                  v-model="subscriptionData.billingEnabled"
                  :label="$t('Enabled Billing?')"
                  name="billing-enabled"
                  readonly
                />
              </b-col>

              <b-col
                md="2"
                class="mt-2"
              >
                <SwitchButton
                  v-model="subscriptionData.activeAccountsOnly"
                  :label="$t('Active Account Only?')"
                  name="active-account-only"
                  readonly
                />
              </b-col>
              <b-col md="4">
                <InputNumberField
                  id="minimum-billing-amount"
                  v-model="subscriptionData.minimumBillingAmount"
                  :label="$t('Minimum Billing Amount')"
                  readonly
                />
              </b-col>
              <b-col
                md="4"
              >
                <SelectField
                  id="billing-frequency"
                  v-model="subscriptionData.billingFrequency"
                  :label="$t('Billing Frequency')"
                  :options="translatedBillingFrequencyOptions"
                  :placeholder="$t('Select')"
                  :reduce="option => option.value"
                  item="label"
                  readonly
                />
              </b-col>

              <b-col md="4">
                <InputField
                  id="billing-commencement-date"
                  v-model="subscriptionData.billingCommencementDate"
                  type="text"
                  :label="$t('Billing Commencement Date')"
                  readonly
                />
              </b-col>
              <b-col md="4">
                <InputField
                  id="unit-price"
                  v-model="subscriptionData.unitPrice"
                  type="text"
                  :label="$t('Unit Price')"
                  readonly
                />
              </b-col>
              <b-col
                v-if="subscriptionData.discountFlatAmount > 0"
                md="4"
              >
                <InputNumberField
                  id="discount-flat-amount"
                  v-model="subscriptionData.discountFlatAmount"
                  :label="$t('Discount Flat Amount')"
                  readonly
                />
              </b-col>
              <b-col
                v-if="subscriptionData.discountPercentage > 0"
                md="4"
              >
                <InputNumberField
                  id="discount-percentage"
                  v-model="subscriptionData.discountPercentage"
                  :label="$t('Discount Percentage')"
                  readonly
                />
              </b-col>
              <b-col md="4">
                <InputField
                  id="maximum-days-overdue"
                  v-model="subscriptionData.maximumDaysOverdue"
                  type="text"
                  :label="$t('Maximum Days Overdue')"
                  readonly
                />
              </b-col>
              <b-col
                md="4"
                class="mt-2"
              >
                <SwitchButton
                  v-model="subscriptionData.includeEmailCharge"
                  :label="$t('Include Email Charge?')"
                  name="include-email-charge"
                  readonly
                />
              </b-col>
              <b-col md="4">
                <InputNumberField
                  id="email-charge"
                  v-model="subscriptionData.emailCharge"
                  :label="$t('Email Charge')"
                  readonly
                />
              </b-col>
              <b-col md="4">
                <InputField
                  id="vat-percentage"
                  v-model="subscriptionData.vatPercentage"
                  type="text"
                  :label="$t('VAT Percentage')"
                  readonly
                />
              </b-col>
              <b-col md="4">
                <InputField
                  id="currency"
                  v-model="subscriptionData.currency"
                  type="text"
                  :label="$t('Currency')"
                  readonly
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col md="12">
                <TextAreaField
                  id="notification-emails"
                  v-model="subscriptionData.notificationEmails"
                  :label="$t('Notification Emails')"
                  rows="2"
                  readonly
                />
              </b-col>
            </b-row>
          </b-form>
        </b-col>
      </b-row>
    </div>

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BForm,
} from 'bootstrap-vue'
import { required, positive } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import { toBalance } from '@/helpers/formatAmount'
import TextAreaField from '../../../forms/TextAreaField.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import SelectField from '../../../forms/SelectField.vue'
import InputNumberField from '../../../forms/InputNumberField.vue'
import InputField from '../../../forms/InputField.vue'
import SwitchButton from '../../../components/switch/Switch.vue'
import { adminPermissions } from '@/helpers/permissions'
import { toDateWithYear } from '@/helpers/formatDate'

const billingFrequencyOptions = [
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Yearly', label: 'Yearly' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BForm,
    SelectField,
    InputNumberField,
    InputField,
    SwitchButton,
    TextAreaField,
    FullScreenSpinner,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      showSpinner: false,
      isLoading: false,
      required,
      positive,
      subscriptionData: {
        billingEnabled: false,
        billingCommencementDate: null,
        activeAccountsOnly: false,
        unitPrice: '',
        discountFlatAmount: '',
        discountPercentage: '',
        minimumBillingAmount: '',
        billingFrequency: '',
        notificationEmails: '',
        maximumDaysOverdue: '',
        includeEmailCharge: false,
        emailCharge: '',
        vatPercentage: null,
        currency: '',
      },
      // billingFrequencyOptions: ['Daily', 'Weekly', 'Monthly', 'Yearly'],
    }
  },
  computed: {
    ...mapGetters({
      userData: 'auth/getCurrentUser',
    }),
    translatedBillingFrequencyOptions() {
      return billingFrequencyOptions.map(item => ({ ...item, text: item.text ? this.$t(item.text) : item.value }))
    },
  },

  async mounted() {
    this.fetchSubscriptionConfig()
  },
  methods: {
    ...mapActions({

      getSubscriptionConfig: 'admin/getSubscriptionConfigAction',
    }),

    async fetchSubscriptionConfig() {
      try {
        this.showSpinner = true
        const response = await this.getSubscriptionConfig({ permission: adminPermissions.VIEW_SUBSCRIPTION_CONFIG })
        const { status, data } = await response
        if (status === 200 && data) {
          this.subscriptionData = {
            ...data,
            discountFlatAmount: data.discountFlatAmount > 0 ? toBalance(data.discountFlatAmount) : '',
            discountPercentage: data.discountPercentage > 0 ? toBalance(data.discountPercentage) : '',
            emailCharge: data.emailCharge > 0 ? toBalance(data.emailCharge) : '',
            minimumBillingAmount: data.minimumBillingAmount > 0 ? toBalance(data.minimumBillingAmount) : '',
            billingCommencementDate: data.billingCommencementDate ? toDateWithYear(data.billingCommencementDate) : '',
          }
        }
        this.showSpinner = false
      } catch (error) {
        this.alertError({ text: error })
      }
    },
  },
}
</script>
